import {
  QuotaData,
  getUtilizationColor,
  getUtilizationBg,
  formatLastUpdated,
  getDataFreshness,
  formatGPUCardDisplay,
  getFamilyDisplayName,
} from './quotaUtils';

interface Props {
  quota: QuotaData;
  onRequestIncrease: () => void;
}

export default function ProviderQuotaCard({ quota, onRequestIncrease }: Props) {
  const gpuDisplay = formatGPUCardDisplay(quota);

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'increasing':
        return (
          <svg className="w-4 h-4 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
          </svg>
        );
      case 'decreasing':
        return (
          <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6" />
          </svg>
        );
      default:
        return (
          <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14" />
          </svg>
        );
    }
  };

  // Calculate percentage for circular progress
  const circumference = 2 * Math.PI * 45;
  const strokeDashoffset = circumference - (quota.utilization_percent / 100) * circumference;

  return (
    <div className={`rounded-lg border p-4 hover:shadow-lg transition-shadow ${getUtilizationBg(quota.utilization_percent)}`}>
      <div className="flex items-start justify-between mb-3">
        <div>
          <h4 className="font-semibold text-white text-lg">{getFamilyDisplayName(quota.provider, quota.instance_family)}</h4>
          <p className="text-sm text-gray-400">{quota.region_display_name || quota.region}</p>
        </div>

        {/* Circular Progress Indicator */}
        <div className="relative w-20 h-20">
          <svg className="transform -rotate-90" width="80" height="80">
            <circle
              cx="40"
              cy="40"
              r="35"
              stroke="currentColor"
              strokeWidth="8"
              fill="none"
              className="text-gray-700"
            />
            <circle
              cx="40"
              cy="40"
              r="35"
              stroke="currentColor"
              strokeWidth="8"
              fill="none"
              strokeDasharray={circumference}
              strokeDashoffset={strokeDashoffset}
              className={getUtilizationColor(quota.utilization_percent)}
              strokeLinecap="round"
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <span className={`text-lg font-bold ${getUtilizationColor(quota.utilization_percent).split(' ')[0]}`}>
              {Math.round(quota.utilization_percent)}%
            </span>
          </div>
        </div>
      </div>

      {/* Usage Details */}
      <div className="space-y-2 mb-3">
        <div className="flex justify-between text-sm">
          <span className="text-gray-400">Usage</span>
          <span className="text-white font-medium">
            {quota.current_usage} / {quota.quota_limit}
            {quota.provider === 'aws' && <span className="text-gray-500 ml-1">vCPUs</span>}
            {quota.provider === 'gcp' && (
              <span className="text-gray-500 ml-1">GPUs</span>
            )}
          </span>
        </div>
        {gpuDisplay && (
          <div className="flex justify-between text-sm">
            <span className="text-gray-400">Est. GPUs</span>
            <span className="text-blue-400 font-medium">{gpuDisplay}</span>
          </div>
        )}
        <div className="flex justify-between text-sm">
          <span className="text-gray-400">Available</span>
          <span className="text-green-400 font-medium">
            {quota.available}
            {quota.provider === 'aws' && <span className="text-gray-500 ml-1">vCPUs</span>}
            {quota.provider === 'gcp' && (
              <span className="text-gray-500 ml-1">GPUs</span>
            )}
          </span>
        </div>
        <div className="flex justify-between text-sm items-center">
          <span className="text-gray-400">Trend</span>
          <div className="flex items-center space-x-1">
            {getTrendIcon(quota.usage_trend)}
            <span className="text-gray-300 capitalize">{quota.usage_trend}</span>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between pt-3 border-t border-gray-700">
        <div className="flex items-center space-x-2">
          <span className="text-xs text-gray-500">
            Updated {formatLastUpdated(quota.last_updated)}
          </span>
          <span className={`text-xs px-1.5 py-0.5 rounded border ${getDataFreshness(quota.last_updated).bgColor} ${getDataFreshness(quota.last_updated).color}`}>
            {getDataFreshness(quota.last_updated).status}
          </span>
        </div>

        {!quota.quota_increase_requested && (
          <button
            onClick={onRequestIncrease}
            className={`text-xs px-2 py-1 rounded transition-colors ${
              quota.utilization_percent >= 80
                ? 'bg-blue-600 hover:bg-blue-700 text-white'
                : 'bg-gray-700 hover:bg-gray-600 text-gray-300 border border-gray-600'
            }`}
          >
            Request Increase
          </button>
        )}

        {quota.quota_increase_requested && (
          <span className="text-xs px-2 py-1 bg-yellow-900/30 text-yellow-300 border border-yellow-700 rounded flex items-center space-x-1">
            <svg className="w-3 h-3 animate-pulse" fill="currentColor" viewBox="0 0 8 8">
              <circle cx="4" cy="4" r="3" />
            </svg>
            <span>Increase Pending</span>
          </span>
        )}
      </div>
    </div>
  );
}